import React from 'react';
import { useTranslation } from 'react-i18next';

interface GasStationContentProps {
  gasStation?: any;
}

const FUEL_KEYS = [
  'diesel',
  'sp95',
  'e10',
  'sp98',
  'e85',
  'lpg',
  'adblue',
  'cng',
  'electricity',
  'gnr',
] as const;

const SERVICE_KEYS = [
  'selfService',
  'carWash',
  'toilets',
  'shop',
  'atm',
  'compressedAir',
  'wheelchair',
  'hgv',
] as const;

function formatAddress(address?: {
  housenumber?: string | null;
  street?: string | null;
  postcode?: string | null;
  city?: string | null;
} | null): string | null {
  if (!address) {
    return null;
  }
  const streetLine = [address.housenumber, address.street].filter(Boolean).join(' ');
  const cityLine = [address.postcode, address.city].filter(Boolean).join(' ');
  const line = [streetLine, cityLine].filter(Boolean).join(', ');
  return line || null;
}

function isAffirmative(value: string): boolean {
  return value.trim().toLowerCase() === 'yes';
}

function websiteHref(website: string): string {
  if (/^https?:\/\//i.test(website)) {
    return website;
  }
  return `https://${website}`;
}

const GasStationContent: React.FC<GasStationContentProps> = ({ gasStation }) => {
  const { t } = useTranslation();

  if (!gasStation) {
    return (
      <div style={{ marginBottom: '16px', color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
        {t('gasStation.empty')}
      </div>
    );
  }

  const properties = gasStation.properties || {};
  const distance = properties.distance?.calculated;
  const address = formatAddress(properties.address);
  const showBrand = Boolean(properties.brand) && properties.brand !== properties.name;
  const fuelLabels = FUEL_KEYS
    .filter((key) => properties.fuels?.[key])
    .map((key) => t(`gasStation.fuels.${key}`));
  const services = SERVICE_KEYS
    .map((key) => {
      const value = properties.services?.[key];
      if (typeof value !== 'string' || !value.trim()) {
        return null;
      }
      return { key, value: value.trim() };
    })
    .filter((service): service is { key: typeof SERVICE_KEYS[number]; value: string } => service != null);

  const serviceText = (service: { key: typeof SERVICE_KEYS[number]; value: string }) => {
    const label = t(`gasStation.services.${service.key}`);
    if (isAffirmative(service.value)) {
      return label;
    }
    const valueKey = `gasStation.serviceValues.${service.value.toLowerCase()}`;
    const translatedValue = t(valueKey);
    const valueLabel = translatedValue === valueKey ? service.value : translatedValue;
    return `${label} : ${valueLabel}`;
  };

  return (
    <>
      {properties.name && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.name')} :</strong> {properties.name}
        </div>
      )}

      {showBrand && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.brand')} :</strong> {properties.brand}
        </div>
      )}

      {properties.operator && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.operator')} :</strong> {properties.operator}
        </div>
      )}

      {address && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.address')} :</strong> {address}
        </div>
      )}

      {properties.openingHours && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.openingHours')} :</strong> {properties.openingHours}
        </div>
      )}

      {distance != null && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.distance')} :</strong> {Math.floor(distance)} m
        </div>
      )}

      {fuelLabels.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.fuelsTitle')} :</strong> {fuelLabels.join(', ')}
        </div>
      )}

      {services.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.servicesTitle')} :</strong>
          <div>
            {services.map((service) => (
              <div key={service.key}>
                {serviceText(service)}
              </div>
            ))}
          </div>
        </div>
      )}

      {properties.phone && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.phone')} :</strong> {properties.phone}
        </div>
      )}

      {properties.website && (
        <div style={{ marginBottom: '16px' }}>
          <strong style={{ color: '#333' }}>{t('gasStation.website')} :</strong>{' '}
          <a href={websiteHref(properties.website)} target="_blank" rel="noreferrer">{properties.website}</a>
        </div>
      )}
    </>
  );
};

export default GasStationContent;
